// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Modules
import { useFormik } from 'formik';
import * as Yup from 'yup';
// Design System
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
// Features
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
// Utils
import { FetchMethodsEnum, fetchTheAPI, TENANCY_HTTP_HEADER } from '@utmb/utils/utmb';
// Styles
import styles from './preregistration.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { Radio } from '@utmb/design-system/radio/Radio';
import { Button } from '@utmb/design-system/button/Button';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import MoreInfoDialog from '../modal-more-info/MoreInfoDialog';
import { Checkbox } from '@utmb/design-system/checkbox/Checkbox';
import { BoostReasonEnum } from '@utmb/types/enum/BoostReasonEnum';
import { RacePreregistrationOpenView } from '@utmb/types/index';
import { Slide, toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BoostChoice } from './components/boost-choice/boost-choice';
import { BoostStatusMessage } from './components/boost-status-message/boost-status-message';

interface PreregistrationProps {
    item: RacePreregistrationOpenView;
    onCancel: () => void;
    validate: () => void;
}

export const PreregistrationForm = (props: PreregistrationProps): ReactElement | null => {
    const { onCancel, item, validate } = props;
    const { t } = useTranslation();
    const { tenantId } = useTenancy();
    const [isDialogHelpOpen, setIsDialogHelpOpen] = useState<boolean>(false);
    const [disableButton, setDisableButton] = useState<boolean>(false);

    const noInputError = t('contactForm.noInputError');

    const operatorSuffix = item.operator === 'utmb' || item.operator === 'tig' ? item.operator : '';

    const generateRandomGroupId = (): string => {
        let result = 'G';
        const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        const charactersLength = characters.length;
        for (let i = 0; i < 5; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            raceYearId: item.raceYearId,
            isGroup: '',
            isGroupNumber: '',
            groupNumber: '',
            requestBoost: undefined as boolean | undefined,
            conditionAcceptance: false,
            conditionAcceptEmail: false,
            urlDashboard: window.location.href,
        },
        validationSchema: Yup.object({
            isGroup: item.noGrp ? Yup.string() : Yup.string().required(noInputError),

            groupNumber: Yup.string().when('isGroup', {
                is: '1', // La valeur qui active la validation (dans ce cas, "1" si isGroup est cochée)
                then: (schema) =>
                    schema
                        .required(noInputError) // Message d'erreur si groupNumber est requis
                        .min(4),
                otherwise: (schema) => schema, // Si isGroup n'est pas cochée, aucune validation requise
            }),

            isGroupNumber: Yup.string().when('isGroup', {
                is: '1',
                then: Yup.string().required(noInputError),
                otherwise: (schema) => schema,
            }),

            conditionAcceptance: Yup.boolean().test('testIsConditionAccept', noInputError, (value, context) => {
                const { conditionAcceptance } = context.parent;
                return conditionAcceptance || value;
            }),

            conditionAcceptEmail: Yup.boolean().test('testIsConditionAcceptEmail', noInputError, (value, context) => {
                const { conditionAcceptEmail } = context.parent;
                return conditionAcceptEmail || value;
            }),
        }),
        onSubmit: async (values) => {
            const token = await getKeycloakToken();

            const dto = {
                raceYearId: values.raceYearId,
                groupNumber: values.isGroup === '1' ? values.groupNumber : '',
                requestBoost: values.requestBoost ?? undefined,
                urlDashboard: values.urlDashboard ?? undefined,
            };

            let canContinue = true;

            if (dto.groupNumber) {
                //Check if group is not full...

                setDisableButton(true);

                await fetch(`${process.env.NEXT_PUBLIC_FRONTEND_API_URL}/registration/pre-registrations-group/${dto.groupNumber}`, {
                    method: FetchMethodsEnum.GET,
                    headers: {
                        'content-type': 'application/json',
                        [TENANCY_HTTP_HEADER]: tenantId,
                    },
                })
                    .then((response) => {
                        if (response.status !== 200) {
                            console.log(response);
                            alert(t('races.preregistration.groupFull'));
                            canContinue = false;
                            setDisableButton(false);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                        setDisableButton(false);
                    });
            }

            if (canContinue) {
                /*try {
                    if (window.dataLayer && Array.isArray(window.dataLayer)) {
                        window.dataLayer.push({
                            event: 'lotteryformSubmission',
                            formType: 'Lottery Form',
                            formId: `Lottery-${tenantId}-${values.raceYearId}`,
                            url: window.location.href,
                        });
                    }
                } catch (err) {
                    console.error(err);
                }*/

                setDisableButton(true);

                fetchTheAPI({
                    isClient: true,
                    tenantId,
                    bearer: token,
                    method: FetchMethodsEnum.POST,
                    path: `/registration/pre-register-race-year`,
                    body: dto,
                })
                    .then((value) => {
                        if (value) {
                            validate();
                        } else {
                            setDisableButton(false);
                            alert('Sorry, something went wrong');
                        }
                    })
                    .catch((error) => {
                        setDisableButton(false);
                        alert(error);
                    });
            }
        },
    });

    const { handleSubmit, values } = formik;

    useEffect(() => {
        if (values.isGroupNumber === '1') {
            formik.getFieldHelpers('groupNumber').setValue('');
        } else if (values.isGroupNumber === '0') {
            formik.getFieldHelpers('groupNumber').setValue(generateRandomGroupId());
        }
    }, [values.isGroupNumber]);

    if (!item || !item.canRegister) {
        return null;
    }

    function handleCopyButtonPress(code: string) {
        navigator.clipboard.writeText(code);
        toast.success(t('races.preregistration.groupNumberCopied'));
    }

    return (
        <>
            <form onSubmit={handleSubmit} className={styles.rootForm}>
                <Font.FuturaMedium mobile="22" desktop="32" className={styles.title} as="h2">
                    {t('races.preregistration.title')}
                </Font.FuturaMedium>
                {item.boostPct && item.boostReason === BoostReasonEnum.LOYALTY ? (
                    <div className={styles.wrapBoost}>
                        <BoostStatusMessage value percentage={item.boostPct} label={t('races.preregistration.boostLoyaltyReason', { pct: item.boostPct })} />
                    </div>
                ) : (item.boostPct && item.hasBoost) || item.needCustomCode ? (
                    <div className={styles.wrapBoost}>
                        <BoostChoice
                            value={formik.values.requestBoost}
                            percentage={item.boostPct}
                            callback={(value: boolean) => (value !== formik.values.requestBoost ? formik.setFieldValue('requestBoost', value) : null)}
                            specialMode={item.hasBoost ? 'hasBoost' : 'needCustomCode'}
                        />
                    </div>
                ) : item.boostPct && item.boostUtmbGo ? (
                    <div className={styles.wrapBoost}>
                        <BoostChoice
                            value={formik.values.requestBoost}
                            percentage={item.boostPct}
                            callback={(value: boolean) => (value !== formik.values.requestBoost ? formik.setFieldValue('requestBoost', value) : null)}
                        />
                    </div>
                ) : null}
                {!item.noGrp ? (
                    <Font.OxaniumBold mobile="14" desktop="16" as="div" className={styles.row_question}>
                        <div>
                            <p className={styles.label_question}>
                                {t('races.preregistration.groupQuestion')}
                                <Button.Ghost onPress={() => setIsDialogHelpOpen(true)}>
                                    <img className={styles.picto} src={`/svg/my-utmb-finals/informations.svg`} alt="information" />
                                </Button.Ghost>
                            </p>
                        </div>
                        <ul className={styles.radio_list}>
                            <li className={styles.radio_item_container}>
                                <Radio
                                    id="isGroupYes"
                                    name="isGroup"
                                    label={t('common.yes')}
                                    value="1"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </li>
                            <li className={styles.radio_item_container}>
                                <Radio
                                    id="isGroupNo"
                                    name="isGroup"
                                    label={t('common.no')}
                                    value="0"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </li>
                        </ul>
                        {formik.touched.isGroup && formik.errors.isGroup && <InputMessage type="isGroup" message={formik.errors.isGroup} />}
                    </Font.OxaniumBold>
                ) : (
                    <Font.OxaniumBold mobile="14" desktop="16" as="div" className={styles.row_question}>
                        {t('races.preregistration.noGroupForThisRace')}
                    </Font.OxaniumBold>
                )}

                {values.isGroup === '1' && (
                    <>
                        <Font.OxaniumBold mobile="14" desktop="16" as="div" className={styles.row_question}>
                            <div>{t('races.preregistration.questionAlreadyHaveGroupNumber')}</div>
                            <ul className={styles.radio_list}>
                                <li className={styles.radio_item_container}>
                                    <Radio
                                        id="isGroupNumberYes"
                                        name="isGroupNumber"
                                        label={t('common.yes')}
                                        value="1"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </li>
                                <li className={styles.radio_item_container}>
                                    <Radio
                                        id="isGroupNumberNo"
                                        name="isGroupNumber"
                                        label={t('common.no')}
                                        value="0"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </li>
                            </ul>
                        </Font.OxaniumBold>
                        {formik.touched.isGroup && formik.errors.isGroup && <InputMessage type="isGroup" message={formik.errors.isGroup} />}
                        {values.isGroupNumber === '1' && (
                            <Font.OxaniumBold mobile="14" desktop="16" as="div" className={styles.column_question}>
                                {t('races.preregistration.groupNumberTitle')} :
                                <input
                                    id="groupNumber"
                                    name="groupNumber"
                                    type="text"
                                    maxLength={20}
                                    minLength={4}
                                    placeholder={t('races.preregistration.groupNumberPlaceholder')}
                                    value={formik.values.groupNumber}
                                    className={styles.input_group}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required={values.isGroup === '1'}
                                />
                                {formik.touched.groupNumber && formik.errors.groupNumber && (
                                    <InputMessage type="groupNumber" message={formik.errors.groupNumber} />
                                )}
                            </Font.OxaniumBold>
                        )}
                        {values.isGroupNumber === '0' && (
                            <Font.OxaniumBold mobile="14" desktop="16" as="div" className={styles.column_question}>
                                <div>{t('races.preregistration.newGroupNumberTitle')}</div>
                                <Button.Default
                                    color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                    bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                    label={formik.values.groupNumber}
                                    type="button"
                                    onPress={() => handleCopyButtonPress(formik.values.groupNumber)}
                                ></Button.Default>
                                {formik.touched.groupNumber && formik.errors.groupNumber && (
                                    <InputMessage type="groupNumber" message={formik.errors.groupNumber} />
                                )}
                            </Font.OxaniumBold>
                        )}
                    </>
                )}

                <div className={styles.condition}>
                    <Checkbox
                        id="conditionAcceptance"
                        name="conditionAcceptance"
                        label=""
                        checked={values.conditionAcceptance}
                        value="1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <div dangerouslySetInnerHTML={{ __html: t(`races.preregistration.conditionAccept${operatorSuffix}`) }} />
                </div>

                {formik.touched.conditionAcceptance && formik.errors.conditionAcceptance && (
                    <InputMessage type="conditionAcceptance" message={formik.errors.conditionAcceptance} />
                )}

                <div className={styles.condition}>
                    <Checkbox
                        id="conditionContact"
                        name="conditionAcceptEmail"
                        label=""
                        checked={values.conditionAcceptEmail}
                        value="1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <div dangerouslySetInnerHTML={{ __html: t(`races.preregistration.conditionAcceptEmail${operatorSuffix}`) }} />
                </div>

                {formik.touched.conditionAcceptEmail && formik.errors.conditionAcceptEmail && (
                    <InputMessage type="conditionAcceptEmail" message={formik.errors.conditionAcceptEmail} />
                )}

                <div className={styles.buttonContainer}>
                    <Button.Default
                        color={getColorStyle(ColorTypesEnum.WHITE)}
                        bgcolor={getColorStyle(ColorTypesEnum.DEFAULT)}
                        type="button"
                        label={t('common.cancel')}
                        onPress={onCancel}
                    />
                    <Button.Default
                        disabled={disableButton}
                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                        type="submit"
                        className={[styles.btnConfirm, 'preregBtn'].join(' ')}
                        label={t('common.confirm')}
                    />
                </div>
            </form>

            <MoreInfoDialog
                isOpen={isDialogHelpOpen}
                onClose={() => setIsDialogHelpOpen(false)}
                title={t('races.preregistration.groupTitle')}
                message={`${t('races.preregistration.groupHelp')}${item.isNeedStones ? t('races.preregistration.groupHelpStones') : ''}`}
            />

            <ToastContainer position="bottom-right" hideProgressBar transition={Slide} />
        </>
    );
};
